import { ArrowLeft, Clock, Eye } from 'lucide-react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArticleCommentsPreview } from '@/components/news/ArticleCommentsPreview';
import { ArticleGallery } from '@/components/news/ArticleGallery';
import { RelatedNewsList } from '@/components/news/RelatedNewsList';
import { ShareBar } from '@/components/news/ShareBar';
import { Seo } from '@/components/seo/Seo';
import { appConfig } from '@/config/app';
import { getArticleBySlug, getRelatedNews } from '@/services/newsRepository';
import { formatDate, formatTime } from '@/utils/date';

export function NewsArticlePage() {
  const { slug } = useParams();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return <Navigate to="/" replace />;
  }

  const related = getRelatedNews(article);
  const canonical = `${appConfig.siteUrl}/noticia/${article.slug}`;
  const paragraphs = article.body.split('\n\n');

  return (
    <article className="pb-28">
      <Seo
        title={`${article.seoTitle} | El Divergente`}
        description={article.metaDescription}
        canonical={canonical}
        keywords={article.metaKeywords}
        image={article.hero.url}
        type="article"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'NewsArticle',
          headline: article.title,
          description: article.metaDescription,
          image: [article.hero.url, ...article.gallery.map((item) => item.url)],
          datePublished: article.publishedAt,
          dateModified: article.updatedAt,
          author: { '@type': 'Person', name: article.author.name },
          publisher: { '@type': 'Organization', name: appConfig.name },
          mainEntityOfPage: canonical,
          articleSection: article.category,
          keywords: article.metaKeywords.join(', ')
        }}
      />
      <div className="relative">
        <img src={article.hero.url} alt={article.hero.alt} className="h-[58dvh] w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/35 to-black/10" />
        <Link
          to="/"
          className="safe-px absolute left-0 top-4 inline-flex min-h-10 items-center gap-2 text-sm font-semibold text-white"
        >
          <ArrowLeft size={18} aria-hidden="true" />
          Feed
        </Link>
      </div>
      <div className="safe-px -mt-8 space-y-6">
        <div className="rounded bg-divergent-surface p-5 shadow-app">
          <div className="mb-3 flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-white/70">
            <span className="rounded bg-divergent-yellow px-2 py-1 text-black">{article.category}</span>
            <span>{formatTime(article.publishedAt)}</span>
            <span className="inline-flex items-center gap-1">
              <Clock size={14} aria-hidden="true" />
              {article.readingTimeMinutes} min
            </span>
            <span className="inline-flex items-center gap-1">
              <Eye size={14} aria-hidden="true" />
              {article.views.toLocaleString('es-AR')}
            </span>
          </div>
          <h1 className="text-3xl font-black leading-tight">{article.title}</h1>
          <p className="mt-3 text-white/72">{article.summary}</p>
          <div className="mt-4 grid gap-1 text-sm text-white/60">
            <span>Por {article.author.name}</span>
            <span>{formatDate(article.publishedAt)}</span>
          </div>
          <div className="mt-5">
            <ShareBar payload={{ title: article.title, text: article.summary, url: canonical }} />
          </div>
        </div>
        <ArticleGallery items={article.gallery} />
        <div className="space-y-5 text-lg leading-8 text-white/86">
          {paragraphs.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {article.tags.map((tag) => (
            <Link
              key={tag}
              to={`/buscar?categoria=${encodeURIComponent(article.category)}`}
              className="rounded border border-white/10 px-3 py-2 text-sm text-white/62 hover:border-divergent-yellow hover:text-divergent-yellow"
            >
              #{tag}
            </Link>
          ))}
        </div>
        <ArticleCommentsPreview />
        <RelatedNewsList articles={related} />
      </div>
    </article>
  );
}
